import { useSession } from "@/services/auth0/session";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import type { ServiceArea } from "@/api.types.v2";

export type { ServiceArea };

const API_URL = import.meta.env.VITE_API_URL;

export interface Address {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// ========================= Service Area Schemas =========================

const GeoJSONPolygonSchema = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(z.array(z.array(z.number()))),
});

export const createServiceAreaSchema = z.object({
  code: z
    .string()
    .min(1, "Code is required")
    .regex(
      /^[a-z0-9_]+$/,
      "Code must be lowercase alphanumeric with underscores"
    ),
  name: z.string().min(1, "Name is required"),
  isActive: z.boolean().optional(),
  boundary: GeoJSONPolygonSchema,
  postcodes: z.array(z.string()).optional().default([]),
  maxDailyOrders: z
    .number()
    .min(0, "Max daily orders must be non-negative")
    .optional(),
  maxVehicles: z
    .number()
    .min(0, "Max vehicles must be non-negative")
    .optional(),
  metadata: z.object({}).passthrough().optional().nullable(),
});

export const updateServiceAreaSchema = createServiceAreaSchema.partial();

export type CreateServiceAreaFormData = z.infer<typeof createServiceAreaSchema>;
export type UpdateServiceAreaFormData = z.infer<typeof updateServiceAreaSchema>;

// ========================= API Hooks =========================

/**
 * Find service area for a given postal code (simplified approach)
 */
export function useFindServiceArea(address?: Address) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["service-area", address?.postalCode],
    queryFn: async () => {
      if (!address?.postalCode) return null;

      // For now, just get the first available service area
      // This is a simplified approach until backend has proper address lookup
      const { data } = await axios.get<ServiceArea[]>(
        `${API_URL}/api/v2/service-areas`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Return first active service area as fallback
      return data.find((sa) => sa.isActive) ?? null;
    },
    enabled: !!token && !!address,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get all service areas
 */
export function useGetServiceAreas() {
  const { token } = useSession();

  return useQuery({
    queryKey: ["service-areas"],
    queryFn: async () => {
      const { data } = await axios.get<ServiceArea[]>(
        `${API_URL}/api/v2/service-areas`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get default service area (fallback)
 */
export function useDefaultServiceArea() {
  const { token } = useSession();

  const { data: serviceAreas } = useGetServiceAreas();

  return useQuery({
    queryKey: ["default-service-area"],
    queryFn: () => {
      // Return the first active service area as default
      return serviceAreas?.find((sa) => sa.isActive) ?? null;
    },
    enabled: !!token && !!serviceAreas,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

// ========================= Service Area Mutations =========================

/**
 * Create a new service area
 */
export function useCreateServiceArea(options?: {
  onSuccess?: (data: ServiceArea) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateServiceAreaFormData) => {
      const { data: response } = await axios.post<ServiceArea>(
        `${API_URL}/api/v2/service-areas`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["service-areas"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Update an existing service area
 */
export function useUpdateServiceArea(options?: {
  onSuccess?: (data: ServiceArea) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      serviceAreaId,
      data,
    }: {
      serviceAreaId: string;
      data: UpdateServiceAreaFormData;
    }) => {
      const { data: response } = await axios.patch<ServiceArea>(
        `${API_URL}/api/v2/service-areas/${serviceAreaId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["service-areas"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/**
 * Delete a service area
 */
export function useDeleteServiceArea(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceAreaId: string) => {
      await axios.delete(`${API_URL}/api/v2/service-areas/${serviceAreaId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-areas"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
// Alias for backward compatibility
export const useServiceAreas = useGetServiceAreas;
